import { createStyles, Group, Menu, Stack } from '@mantine/core';
import { Icon, IconButton, SplitButton, Text } from '@repo/foundations';

import { type PolicyViolationOut } from '@repo/api-codegen';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import policyIssueStore from '../store';

const useStyles = createStyles(() => ({
	title: {
		flexFlow: 'nowrap',
	},
}));

interface IssueActionBarProps {
	violations: PolicyViolationOut[];
	handleAcknowledgeOnClick: (resultId: string) => void;
	handleResolveOnClick: (resultId: string) => void;
	handleIgnoreOnClick: (resultId: string) => void;
	handleReopenOnClick: (resultId: string) => void;
	readOnly?: boolean;
}

export const IssueActionBar = observer(
	({
		violations,
		handleAcknowledgeOnClick,
		handleResolveOnClick,
		handleIgnoreOnClick,
		handleReopenOnClick,
		readOnly = false,
	}: IssueActionBarProps) => {
		const { classes, theme } = useStyles();

		const isIgnored = useMemo(
			() => violations.some((violation) => !isNil(violation.ignored_at)),
			[violations]
		);

		const label = useMemo(() => {
			if (violations.some((violation) => !isNil(violation.acknowledged_at))) {
				return 'Resolve';
			}

			return 'Acknowledge';
		}, [violations]);

		const menuOptions = useMemo(() => {
			const actions = [];

			if (!violations.some((violation) => !isNil(violation.acknowledged_at))) {
				actions.push({
					label: 'Resolve',
					onClick: handleResolveOnClick,
				});
			}
			actions.push({
				label: 'Ignore',
				onClick: handleIgnoreOnClick,
			});

			return actions;
		}, [violations, handleResolveOnClick, handleIgnoreOnClick]);

		const resolved = useMemo(
			() => violations.some((violation) => !isNil(violation.resolved_at)),
			[violations]
		);

		return (
			<Group
				p={`${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.md}`}
				position="apart"
				noWrap
			>
				<Text
					size="sm"
					fw={500}
					p={`${theme.spacing['3xs']} ${theme.spacing.xs}`}
				>
					Issue details
				</Text>
				<Group className={classes.title} spacing={theme.spacing.xs}>
					{!resolved && !isIgnored && !readOnly && (
						<SplitButton
							variant="primary"
							onClick={
								label === 'Acknowledge'
									? () => handleAcknowledgeOnClick(violations[0].id)
									: () => handleResolveOnClick(violations[0].id)
							}
							label={label}
						>
							<Menu>
								<Menu.Target>
									<Icon name="chevronDown" />
								</Menu.Target>
								<Menu.Dropdown>
									{menuOptions.map((option) => (
										<Menu.Item
											key={option.label}
											onClick={() => option.onClick(violations[0].id)}
										>
											{option.label}
										</Menu.Item>
									))}
								</Menu.Dropdown>
							</Menu>
						</SplitButton>
					)}
					{isIgnored && !readOnly && (
						<Menu>
							<Menu.Target>
								<Icon name="dots" />
							</Menu.Target>
							<Menu.Dropdown>
								<Menu.Item
									key="Re-open"
									onClick={() => handleReopenOnClick(violations[0].id)}
								>
									<Stack spacing={0}>
										<Text size="sm">Re-open issue</Text>
										<Text size="xs" color="text/secondary/default">
											This will open the issue.
										</Text>
									</Stack>
								</Menu.Item>
							</Menu.Dropdown>
						</Menu>
					)}
					<IconButton
						onClick={policyIssueStore.closeEntityDrawer}
						iconName="x"
						variant="tertiary"
					/>
				</Group>
			</Group>
		);
	}
);
